import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, useAuthActions } from '@frontegg/react';
import { Form, Select, Button, Input, Card, Avatar } from 'antd';
import { format } from 'date-fns';
import {
  ArrowLeftOutlined,
  CheckOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import { useForm } from 'antd/es/form/Form';
import { ScreenLoader } from 'src/components';
import styled from '@emotion/styled';
import { useGetSpecialityList } from 'src/hooks/speciality';
import { celloReferralSourceOption, referralSourceOptions } from '.';
import { useGetPractitionerWorkspaceList } from 'src/hooks/practitioner';
import { useGetEhrList } from 'src/hooks/ehr';
import { analytics } from 'src/services';
import {
  useOnboardPractitioner,
  useOnboardPractitionerToWorkspace,
} from 'src/hooks/practitioner';
import { showToastError } from 'src/utilities/errortoast';
import { settingsOrgFlag } from 'src/store/actions/appointment.action';
import { useDispatch, useSelector } from 'react-redux';
import { generateColorFromName } from 'src/utils';
import BackgroundMobile from './background-mobile.jpg';
import BackgroundDesktop from './background.jpg';

const Wrapper = styled.div`
  background-color: #edf8fe;
  background-image: url(${BackgroundMobile});
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;

  @media (min-width: 1024px) {
    background-image: url(${BackgroundDesktop});
  }
`;

const filterOptions = (input: string, option: any) => {
  return (
    (option?.label || '').toLowerCase().includes(input.toLowerCase()) ||
    (option?.label || '').toLowerCase().includes('other')
  );
};

export const Onboarding = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { loadEntitlements } = useAuthActions();
  const dispatch = useDispatch();
  const [form] = useForm();

  const { settingsOrgValue } = useSelector(
    (state: any) => state?.appointmentState
  );
  const isOnboardWorkspaceEntitled =
    !!settingsOrgValue?.entitlements?.features?.['onboard-workspace']
      ?.isEntitled;

  const [view, setView] = useState<'existing' | 'new'>('existing');
  const [celloToken, setCelloToken] = useState<string | null>(null);

  const {
    data: practitionerWorkspaceList,
    isLoading: practitionerWorkspaceListLoading,
  } = useGetPractitionerWorkspaceList();

  const showLoader = !user || practitionerWorkspaceListLoading;

  const { data: specialityList, isLoading: specialityListLoading } =
    useGetSpecialityList();

  const specialityOptions = useMemo(() => {
    return specialityList?.specialties.map((speciality) => ({
      label: speciality.specialty_name,
      value: speciality.specialty_id,
    }));
  }, [specialityList]);

  const { data: ehrList, isLoading: ehrListLoading } = useGetEhrList();

  const canJoinExistingWorkspace = !!(
    practitionerWorkspaceList?.organizations?.length &&
    isOnboardWorkspaceEntitled
  );

  const {
    mutateAsync: onboardPractitioner,
    isPending: isOnboardingPractitioner,
  } = useOnboardPractitioner();
  const {
    mutateAsync: onboardPractitionerToWorkspace,
    isPending: isOnboardingPractitionerToWorkspace,
  } = useOnboardPractitionerToWorkspace();

  const handleJoinExistingWorkspace = async ({
    org_uuid,
  }: {
    org_uuid: string;
  }) => {
    analytics.track('Clicked Join Existing Workspace', {
      email: user?.email,
      org_uuid,
    });
    try {
      await onboardPractitionerToWorkspace({
        org_uuid,
      });
      window.location.href = '/';
    } catch (error) {
      showToastError('Something went wrong!');
    }
  };

  const handleCompleteOnboarding = async () => {
    analytics.track('Clicked Complete Onboarding', { email: user?.email });
    const values = await form.validateFields();
    const { orgName, speciality, ehr, referralSource } = values;

    const payload = {
      org_name: orgName,
      specialities: [speciality.label] as [string],
      ehr_system: ehr?.label || undefined,
      referral_source: referralSource,
    };

    if (celloToken) {
      Object.assign(payload, {
        referral_code: celloToken,
      });
    }

    try {
      await onboardPractitioner(payload);
      await new Promise<void>((resolve, reject) => {
        const timeout = setTimeout(() => {
          reject(new Error('Entitlements load timed out'));
        }, 5000);

        loadEntitlements({
          callback: (success: boolean) => {
            clearTimeout(timeout);
            if (success) {
              resolve();
            } else {
              reject(new Error('Entitlements load failed'));
            }
          },
        });
      });
      /**
       * @todo: Harshit | Remove this post migration
       */
      await dispatch<any>(settingsOrgFlag());
      navigate('/');
    } catch (error) {
      if (
        error instanceof Error &&
        (error.message === 'Entitlements load failed' ||
          error.message === 'Entitlements load timed out')
      ) {
        window.location.href = '/';
        return;
      }
      showToastError('Something went wrong!');
    }
  };

  useEffect(() => {
    loadEntitlements();
    analytics.track('Viewed Onboarding Screen', { email: user?.email });

    // https://docs.cello.so/docs/connect-attribution#celloattributiongetucc
    (window as any).CelloAttribution?.('getUcc').then((result: string) => {
      if (result) {
        setCelloToken(result);
        form.setFieldsValue({
          referralSource: celloReferralSourceOption.value,
        });
      }
    });
  }, []);

  const renderExistingWorkspace = () => {
    return (
      <Card>
        <div className="flex flex-col w-full gap-5">
          <div className="flex flex-col gap-2">
            <div className="text-2xl font-semibold text-gray-700 text-center">
              Do you want to join your team?
            </div>
            <div className="font-medium text-gray-500 text-center">
              We found existing account with members matching your email domain
            </div>
          </div>
          <div className="flex flex-col gap-4">
            {practitionerWorkspaceList?.organizations.map((organization) => {
              const createdAt = new Date(organization.created_at);
              const formattedDate = format(createdAt, 'MMM yyyy');
              return (
                <div
                  key={organization.org_id}
                  className="p-3 shadow rounded-md border border-gray-100 flex items-center gap-4"
                >
                  <Avatar
                    size={52}
                    shape="square"
                    style={{
                      flexShrink: 0,
                      textTransform: 'uppercase',
                      color: 'white',
                      backgroundColor: generateColorFromName(
                        organization.org_name
                      ),
                    }}
                  >
                    {organization.org_name?.slice(0, 2)}
                  </Avatar>
                  <div className="flex flex-col gap-1">
                    <div className="text-lg font-semibold text-gray-700">
                      {organization.org_name}
                    </div>
                    <div className="text-sm text-gray-500">
                      {organization.user_count} members
                      <span className="mx-1">•</span>
                      Created {formattedDate}
                    </div>
                  </div>
                  <div className="ml-auto right-0 flex-shrink-0">
                    <Button
                      shape="round"
                      className="ih-button-primary"
                      loading={isOnboardingPractitionerToWorkspace}
                      onClick={() =>
                        handleJoinExistingWorkspace({
                          org_uuid: organization.org_uuid,
                        })
                      }
                    >
                      Join
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
          <div
            className="flex text-lg gap-2 justify-center items-center font-medium text-gray-600 text-center p-4 border-2 border-dashed rounded-md cursor-pointer hover:bg-gray-50"
            onClick={() => setView('new')}
          >
            <PlusOutlined />I want to create a separate account
          </div>
        </div>
      </Card>
    );
  };

  const renderNewOrgForm = () => {
    return (
      <Card>
        <div className="flex flex-col w-full gap-2">
          <div className="flex flex-col gap-2">
            <div className="text-2xl font-semibold text-gray-700 text-center">
              Create new account
            </div>
            <div className="font-medium text-gray-500 mb-4 text-center">
              Setup your new account to get started
            </div>
          </div>
          <Form form={form} layout="vertical">
            <Form.Item
              className="field-wrapper"
              name="orgName"
              label={<span className="label">Organization Name</span>}
              rules={[
                {
                  required: true,
                  message: 'Organization name is required',
                },
              ]}
            >
              <Input className="field" placeholder="Enter organization name" />
            </Form.Item>
            <Form.Item
              className="field-wrapper"
              name="speciality"
              label={<span className="label">Speciality</span>}
              rules={[
                {
                  required: true,
                  message: 'Speciality is required',
                },
              ]}
            >
              <Select
                className="field"
                placeholder="Search speciality"
                showSearch
                labelInValue
                options={specialityOptions}
                loading={specialityListLoading}
                filterOption={filterOptions}
              />
            </Form.Item>
            <Form.Item
              className="field-wrapper"
              name="ehr"
              label={<span className="label">EHR</span>}
            >
              <Select
                className="field"
                showSearch
                labelInValue
                placeholder="Search EHR"
                options={ehrList?.ehrList.map((ehr, index) => ({
                  label: ehr,
                  value: index,
                }))}
                loading={ehrListLoading}
                filterOption={filterOptions}
              />
            </Form.Item>
            <Form.Item
              className="field-wrapper"
              name="referralSource"
              label={<span className="label">Referral Source</span>}
            >
              <Select
                className="field"
                showSearch
                placeholder="Search referral source"
                options={
                  celloToken
                    ? [celloReferralSourceOption]
                    : referralSourceOptions
                }
                disabled={!!celloToken}
                filterOption={filterOptions}
              />
            </Form.Item>
          </Form>
          <div className="flex flex-col gap-2 mt-4">
            <Button
              className="ih-button-primary h-12 w-full"
              loading={isOnboardingPractitioner}
              onClick={handleCompleteOnboarding}
            >
              <CheckOutlined />
              Complete onboarding
            </Button>
            {canJoinExistingWorkspace && (
              <Button
                className="ih-button-outlined h-12 w-full"
                onClick={() => setView('existing')}
              >
                <ArrowLeftOutlined />
                Join an existing account
              </Button>
            )}
          </div>
        </div>
      </Card>
    );
  };

  const renderChildren = () => {
    if (showLoader) {
      return (
        <Card className="p-4">
          <ScreenLoader />
        </Card>
      );
    }

    if (view === 'existing' && canJoinExistingWorkspace) {
      return renderExistingWorkspace();
    }

    return renderNewOrgForm();
  };

  return (
    <Wrapper className="flex h-full w-full justify-center items-center p-5">
      <div className="flex flex-col justify-center h-full w-full lg:w-1/2 lg:max-w-[40%]">
        {renderChildren()}
      </div>
    </Wrapper>
  );
};
